/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  PLATFORM_ID,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  faChevronDown,
  faChevronUp,
  faLocationDot,
  faMap,
  faTimes,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { Accordion } from 'primeng/accordion';
import { firstValueFrom, Subject, take } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { StructuredDataService } from '@usheru-hff/sdk/structured-data';
import {
  TrackingIntersectionHandle,
  TrackingService,
  SHOWTIMES_INTERSECTION_CONFIG,
  TRACKING_SERVICE
} from '@usheru-hff/sdk/tracking';
import {
  Cinema,
  Country,
  getChannelMovieProp,
  getMovieGenres,
  getMovieTags,
  LocationComplete,
  Location as LocationModel,
  Movie,
  NearCity,
  PerformanceGroup,
  ShowtimesFlow,
  PerformanceListWrapper
} from '@usheru-hff/shared/domain';
import { LocationSelectorComponent } from '@usheru-hff/shared/feature-location-selector';
import { ModalMapComponent } from '@usheru-hff/shared/ui-modal-map';
import {
  convertToISODate,
  dateFormatter,
  getDateFromDDMMYYYY,
  LogService,
  ScopedLogger,
  TemplatesService
} from '@usheru-hff/shared/utils';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { PerformanceComponent } from './performance/performance.component';
import { ShowtimeStreamingMapComponent } from './showtime-streaming-map/showtime-streaming-map.component';
import { ShowtimesStoreService } from '../shared/showtimes.service';

interface PanelEvent {
  show: Subject<void>;
  hide: Subject<void>;
}

interface Position {
  lat: number;
  lng: number;
}

interface LocalDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * Component that show cinemas from a location where a movie in available.
 *
 * //TODO barbeitoalex propostal - refractor name to something like "showtime-selector".
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - move component folder to the root of components
 */
@Component({
  selector: 'ush-showtime-streaming-in-cinemas',
  templateUrl: './showtime-streaming-in-cinemas.component.html',
  styleUrls: ['./showtime-streaming-in-cinemas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)' }))])
    ]),
    trigger('listAnimation', [
      transition('* => *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(60px)' }),
            stagger(100, [animate('0.5s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))])
          ],
          { optional: true }
        )
      ])
    ])
  ],
  standalone: false
})
export class ShowtimeStreamingInCinemasComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly channelService = inject(ChannelService);
  private readonly templatesService = inject(TemplatesService);
  private readonly logger: ScopedLogger = inject(LogService).scoped('showtime-in-cinemas');

  @HostBinding('attr.data-page') get attrPage() {
    return this.paginationCounter();
  }

  @HostBinding('attr.data-items-per-page') get attrItemsPerPage() {
    return this.itemsPerPagination;
  }

  @HostBinding('attr.data-items-loaded') get attrItemsLoaded() {
    return this.itemsLoadedAmmount();
  }

  @ViewChild('wrapper') wrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('modalMap') modalMap!: ElementRef<ModalMapComponent>;
  @ViewChild(LocationSelectorComponent, { read: ElementRef<HTMLElement> }) locationSelectorRef: ElementRef<HTMLElement>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('locationSelector') locationSelector: any;

  @ViewChild(Accordion) pAcordion: Accordion;
  @ViewChild(ShowtimeStreamingMapComponent) inlineComponentRef: ShowtimeStreamingMapComponent;

  /**
   * @docsNotRequired
   */
  @Input()
  set movie(movie: Movie) {
    this.hasEmittedShowsLoaded = false;
    this._movie = movie;
    this.showtimeStore.setMovie(movie);

    // Movie site controls the showtimes flow on their conversion page
    // libs/movie-detail/feature-brand-movie-conversion-vs-awareness/src/lib/feature-movie-conversion-page/feature-movie-conversion-page.component.html

    const envNewFlow = this.env.showtimeStreamingConfig?.isNewFlow;
    let propertiesShowtimesFlow;

    if (movie.properties?.movieSiteEnabled) {
      propertiesShowtimesFlow =
        getChannelMovieProp('movieSite.showtimes.flow', this.channelService.channel, movie) ?? 'date';
    } else {
      propertiesShowtimesFlow = getChannelMovieProp('selectShowtimes.flow', this.channelService.channel, movie);

      if (!propertiesShowtimesFlow && !envNewFlow) {
        propertiesShowtimesFlow = 'location';
      }
    }

    const determinedFlow = propertiesShowtimesFlow || (envNewFlow === true ? 'date' : 'location');

    this.showtimesFlow.set(determinedFlow);

    this.checkMovieIsOld();
  }

  get movie() {
    return this._movie;
  }

  _movie: Movie;

  @Input()
  enableRouterNavigation? = true;

  @Input()
  showUsheruLogo = false;

  errorKey = signal<string | undefined>(undefined);
  errorMsg = signal<string | undefined>(undefined);

  selectedLocationCountry: Country;

  /**
   * Counter to count chunks of performances by batches.
   *
   * Check also {@link itemsPerPagination}.
   */
  readonly paginationCounter = signal(0);

  /**
   * Batch Qty of Performances
   *
   * Check also {@link paginationCounter}.
   */
  readonly itemsPerPagination = this.env.showtimeStreamingConfig?.numberOfCinemasOnFirstLoad || 5;

  readonly thereArePerformances = computed(
    () => !!(this.isDateFlow() ? this.performancesUiVisible().length : this.performancesVisible().length)
  );

  /**
   * Boolean to check if all items have loaded on UI
   */
  readonly allItemsVisible = computed(
    () => this.paginationCounter() * this.itemsPerPagination >= this.performances().length
  );

  /**
   * Emit the number of {@link performances} after get update this variable.
   */
  @Output()
  showsLoaded = new EventEmitter<number>();

  @Output()
  showtimeTicketing = new EventEmitter<number>();

  /**
   * @docsNotRequired
   */
  @ViewChildren(PerformanceComponent)
  panels: QueryList<PerformanceComponent>;

  @Input() openFirstPanel = false;

  /**
   * //TODO docs favio
   */
  @Input() inCinemasAddon?: TemplateRef<unknown>;

  _inlineMap = false;
  @Input()
  set inlineMap(inlineMap: boolean) {
    this._inlineMap = inlineMap;
    this.showtimeStore.setInlineMap(inlineMap);
  }
  get inlineMap(): boolean {
    return this._inlineMap;
  }

  readonly showtimesFlow = signal<ShowtimesFlow>('location');

  readonly isDateFlow = computed(() => this.showtimesFlow() === 'date');

  /**
   * @deprecard Not necesary
   */
  params: Params;

  /**
   * Asociated to the actual {@link Router} query params.
   */
  queryParams: Params;

  /**
   * Items to set in the {@link PerformanceComponent}.
   */
  readonly performances = signal<PerformanceGroup[]>([]);

  /**
   * ALL performances are loaded. The pagination is to control how many are visible.
   */
  readonly performancesVisible = computed(() =>
    this.performances().slice(0, this.paginationCounter() * this.itemsPerPagination)
  );

  readonly itemsLoadedAmmount = computed(() => {
    if (this.isDateFlow()) {
      return this.performancesUi().length;
    } else {
      return this.performances().length;
    }
  });

  readonly isPerformancesEmpty = computed(() => (this.performances().length <= 0 ? true : false));

  /**
   * Date selected in the UshShowtimeDatesComponent.
   */
  dateSelected: Date;

  /**
   * UI Items to set in the {@link PerformanceComponent}.
   */
  readonly performancesUi = signal<PerformanceGroup[]>([]);

  /**
   * Same porpurse as {@link performancesVisible} but for the {@link performancesUi}.
   */
  readonly performancesUiVisible = computed(() =>
    this.performancesUi().slice(0, this.paginationCounter() * this.itemsPerPagination)
  );

  readonly allPerformancesUiVisible = computed(
    () => this.paginationCounter() * this.itemsPerPagination >= this.performancesUi().length
  );

  readonly isPerformancesUiEmpty = computed(() => (this.performancesUi().length <= 0 ? true : false));

  /**
   * This count is need to add white space to replace showtime cards when date is changed {@link PerformanceComponent}.
   */
  readonly previousPerformanceUiCount = signal(0);

  /**
   * @deprecard Don't found any use
   */
  panelEvents: PanelEvent[];

  /**
   * Status to know if it is waiting to update {@link performances}.
   */
  readonly loadingShowtimes = signal(true);

  /**
   * Status to know if there are some location loaded.
   */
  readonly hasLocation = signal(false);

  /**
   * @deprecard Don't found any use
   */
  cinema: Cinema;

  /**
   * @deprecard Don't found any use
   */
  indexPanelToOpen = -1;

  /**
   * Actual index reference {@link panels} opened.
   */
  currentIndex = -1;

  /**
   * Active if {@link Channel} has disableShowtimes.
   */
  disableShowtimes = false;

  useNewComponent = false;

  diasbleNearestCities: boolean;

  nearestCities: NearCity[] = [];

  /**
   * Used to track in developvolment the nearest cities changes.
   */
  initialPosition!: LocationModel;

  /**
   * Used to track in developvolment the nearest cities changes.
   */
  nearestCitySetted!: NearCity;

  /**
   * @docsNotRequired
   */
  defaultCinemaPlaceholder = 'https://cdnstatic.usheru.com/img/cinemas/cinemalogo-placeholder.jpg';

  locationIcon = faLocationDot;
  chevronDownIcon = faChevronDown;
  chevronUpIcon = faChevronUp;
  mapIcon = faMap;
  closeIncon = faTimes;
  markerIcon = faMapMarkerAlt;

  // DATE SHOWTIME FLOW
  public showTimeDates: { [key: string]: string } = null;
  showMap: boolean;

  hasMovieTwoYearsOld = signal(false);

  destroy$ = new Subject<void>();
  private showtimesVisibilityHandle?: TrackingIntersectionHandle;
  private hasEmittedShowsLoaded = false;

  private get filterPastLocalShowtimes(): boolean {
    return this.env?.components?.showtimeStreamingInCinemas?.filterPastLocalShowtimes ?? false;
  }

  constructor(
    @Optional() @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    @Inject(PLATFORM_ID) private platform,
    private location: LocationService,
    private loc: Location,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private router: Router,
    private translationService: TranslationsService,
    private movieService: MovieService,
    private showtimeStore: ShowtimesStoreService,
    @Inject(ENVIRONMENT) private env: Environment,
    private showtimeStreamingService: ShowtimeStreamingService,
    private structuredDataService: StructuredDataService
  ) {
    this.disableShowtimes = this.channelService.channel.settings.disableShowtimes;
    this.diasbleNearestCities = this.env.showtimeStreamingConfig?.nearCitiesDisable;
  }

  ngOnInit(): void {
    this.setInitialPosition();
    // if movie is an string then perform the call to backend for the actual movie
  }

  handleCinemaErrorImg(elem) {
    elem.target.src = this.defaultCinemaPlaceholder;
  }

  /**
   * * Listen actual query params.
   *
   * //TODO barbeitoalex propostal - why is this in the afterViewInit? Is good idea trigger this each url change?
   * //TODO barbeitoalex propostal - add the detectChanges to the method where is the problem.
   */
  ngAfterViewInit(): void {
    this.inCinemasAddon = this.inCinemasAddon || this.templatesService.get('inCinemasAddon');
    this.addShowtimesVisibilityListener();
    this.listenRouteParams();
    Promise.resolve().then(() => this.emitShowsLoadedIfNeeded('after-view-init'));
    this.addonLog();
  }

  /**
   * @docsNotRequired
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes?.movie?.firstChange &&
      changes.movie?.currentValue.friendlyUrl != changes.movie?.previousValue.friendlyUrl
    ) {
      // this.restoreInitialPosition();
      this.listenSlug();
    }
  }

  private addShowtimesVisibilityListener(): void {
    if (!this.trackingService) return;
    const { eventName, querySelector } = SHOWTIMES_INTERSECTION_CONFIG(this.env);
    this.debugLog('Preparing showtimes visibility tracking', {
      movie: this.movie?.title,
      eventName,
      querySelector
    });

    if (!this.isBrowser) {
      this.debugLog('Skipping intersection observer setup on server');
      return;
    }

    this.showtimesVisibilityHandle = this.trackingService.usheruSdkTrack({
      kind: 'intersection',
      eventType: 'visibility',
      options: {
        root: null,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.75, 1],
        minIntersectionRatio: 0.05,
        autoReobserve: true,
        debug: false
      },
      eventName,
      querySelector,
      payload: {
        event_category: 'MovieShowtimes',
        label: `${this.movie.title}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    }) as TrackingIntersectionHandle | undefined;
    this.debugLog('Intersection observer configured for ShowtimesViewed', {
      eventName,
      querySelector
    });
  }

  setInitialPosition() {
    this.nearestCitySetted = undefined;
    this.initialPosition = this.location.userLocation;
    this.selectedLocationCountry = this.location.selectedCountry;
  }

  listenRouteParams() {
    this.route.params.subscribe(params => {
      this.onUpdateParams(params);
    });
  }

  onUpdateParams(params: Params) {
    this.params = Object.assign(params, {});
    this.queryParams = Object.assign(this.route.snapshot.queryParams, {});
    this.hasEmittedShowsLoaded = false;
    this.listenSlug();
    this.cd.detectChanges();
  }

  /**
   * Check if there is a query param of cinema to load his location. If it is the case load performances from that
   * location.
   *
   * //TODO barbeitoalex propostal - refractor name. No observable or promise.
   */
  async listenSlug() {
    const cinemaSlug = this.queryParams.cinema;

    let position: Position;
    if (cinemaSlug && !this.disableShowtimes) {
      position = await this.cinemaLocation(cinemaSlug);
    }

    if (position) {
      this.hasLocation.set(true);
      this.loadPerformances(position);
      this.loadNearestCities(position);
    } else {
      this.checkLocation();
    }
  }

  /**
   * Get a {@link Position} from a cinema slug.
   *
   * //TODO barbeitoalex propostal - update to lastValueFrom
   * @param cinemaSlug
   * @returns
   */
  private async cinemaLocation(cinemaSlug: string): Promise<Position> {
    const cinema = await firstValueFrom(this.movieService.getCinema(cinemaSlug)).catch(() => {
      console.error(`No cinema with slug ${cinemaSlug}`);
    });

    if (cinema && cinema.latitude && cinema.longitude) {
      return {
        lat: cinema.latitude,
        lng: cinema.longitude
      };
    }
    return null;
  }

  /**
   * Gets user location from {@link LocationService} and start load performances from that.
   *
   * //TODO barbeitoalex propostal - refractor name. No observable or promise.
   */
  private checkLocation() {
    this.hasLocation.set(!!this.location.userLocation);
    if (this.location.userLocation) {
      const position = {
        lat: this.location.userLocation.latitude,
        lng: this.location.userLocation.longitude
      };
      this.loadPerformances(position);
      this.loadNearestCities(position);
    }
  }

  /**
   * Gets performances from a position.
   *
   * //TODO barbeitoalex propostal - use lastValueFrom.
   * @param position
   */
  private loadPerformances(position: Position) {
    this.debugLog('loadPerformances start', { movie: this.movie?.title, position, flow: this.showtimesFlow() });
    this.loadingShowtimes.set(true);
    this.cd.detectChanges();

    const properties = getChannelMovieProp('selectShowtimes', this.channelService.channel, this.movie) || {};

    const params: PerformanceListWrapper = {
      movie: [(<Movie>this.movie)?.friendlyUrl],
      latitude: position.lat,
      longitude: position.lng,
      trLanguage: this.translationService.currentLanguage().code,
      radius: properties.radius || this.channelService.channel.settings?.showtimeRadius || 50000
    };

    // Using Object.keys()
    if (this.env.performanceQueryExtraFilter) {
      Object.keys(this.env.performanceQueryExtraFilter).forEach(key => {
        const value = this.env.performanceQueryExtraFilter[key];
        params[key] = value;
      });
    }

    firstValueFrom(this.movieService.getPerformancesGroup(params))
      .then((performances = []) => {
        performances = this.evaluateDistanceUnits(performances);
        performances = this.filterPastLocalPerformances(performances);
        // DATE SHOWTIME FLOW
        if (this.isDateFlow()) {
          this.isDateFlow() && this.extractDatesForDateFilter(performances, this.env.performanceStartDateFilter);
        }
        // DATE SHOWTIME FLOW
        if (this.env.performanceQueryResultFilter) {
          performances = performances.filter(perf => this.env.performanceQueryResultFilter(perf));
        }
        this.performances.set(performances);
        this.trackShowtimesEventsFromResult(this.performances().length, 'load-performances', params);

        this.structuredDataService.setPerformanceEventsJsonLd(this.performances(), this.movie);
        if (!this.isDateFlow()) {
          this.resetLoadMoreVariables();
          this.loadMore();
        }
        // initialize the load counter
        this.panelEvents = [];
        this.panelEvents = this.performances().map((perfGroup, i) => {
          if (this.queryParams.cinema && this.queryParams.cinema === perfGroup.cinema.slug) {
            this.cinema = perfGroup.cinema;
            this.indexPanelToOpen = i;
            this.currentIndex = this.indexPanelToOpen;
          }
          return {
            show: new Subject(),
            hide: new Subject()
          };
        });

        if (this.performances() && !this.isPerformancesEmpty()) {
          this.emitShowsLoadedIfNeeded('load-performances');
        } else {
          this.debugLog('No performances found', { movie: this.movie?.title });
        }

        if (this.isPerformancesEmpty()) {
          const error = this.showtimeStreamingService.getShowtimesErrorMessage(this.selectedLocationCountry);
          this.errorKey.set(error.key);
          this.errorMsg.set(error.message);
        }

        this.loadingShowtimes.set(false);
        this.performancesUi.set(performances);
        this.previousPerformanceUiCount.set(this.performancesUi().length);
        this.cd.detectChanges();
        if (this.openFirstPanel) {
          setTimeout(() => {
            this.pAcordion?.tabList.first.toggle(new MouseEvent('click'));
          }, 200);
        }
      })
      .finally(() => {
        this.loadingShowtimes.set(false);
        this.addonLog();
        this.cd.detectChanges();
      });
  }

  /**
   * Removes local showtimes that have already passed and drops performances that no longer
   * contain any visible local times when local filtering is enabled in the browser.
   */
  private filterPastLocalPerformances(performances: PerformanceGroup[]): PerformanceGroup[] {
    if (!this.filterPastLocalShowtimes || !this.isBrowser) {
      return performances;
    }

    return performances
      .map(performance => this.filterPastLocalTimesFromPerformance(performance))
      .filter((performance): performance is PerformanceGroup => !!performance);
  }

  /**
   * Removes past local times from a performance and returns `null` when none remain visible.
   */
  private filterPastLocalTimesFromPerformance(performance: PerformanceGroup): PerformanceGroup | null {
    const localTimes = performance.localTimes
      ?.split(',')
      .map(localTime => localTime.trim())
      .filter(Boolean);

    if (!localTimes?.length) {
      return performance;
    }

    const visibleIndexes = new Set<number>();
    const now = this.getCurrentLocalDateTimeParts();

    localTimes.forEach((localTime, index) => {
      const parsedLocalTime = this.parseLocalTime(localTime);
      if (!parsedLocalTime || this.compareLocalDateTimeParts(parsedLocalTime, now) >= 0) {
        visibleIndexes.add(index);
      }
    });

    if (!visibleIndexes.size) {
      return null;
    }

    if (visibleIndexes.size === localTimes.length) {
      return performance;
    }

    const parsedIds = performance.parsedIds?.filter((_, index) => visibleIndexes.has(index));
    const parsedLocalTimes = performance.parsedLocalTimes?.filter((_, index) => visibleIndexes.has(index)) ?? [];
    const parsedAttributeSlugs = performance.parsedAttributeSlugs?.filter((_, index) => visibleIndexes.has(index));

    return {
      ...performance,
      ids: parsedIds?.join(',') ?? performance.ids,
      localTimes: localTimes.filter((_, index) => visibleIndexes.has(index)).join(','),
      attributeSlugs: parsedAttributeSlugs
        ? parsedAttributeSlugs.map(attributeGroup => `[${attributeGroup.join(',')}]`).join(',')
        : performance.attributeSlugs,
      parsedIds,
      parsedLocalTimes,
      parsedAttributeSlugs
    };
  }

  private parseLocalTime(localTime: string): LocalDateTimeParts | null {
    const [datePart, ...timeParts] = localTime.trim().split(/\s+/);
    const [dayStr, monthStr, yearStr] = datePart?.split('-') ?? [];
    const timePart = timeParts.join('').toUpperCase();
    const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(AM|PM)?$/);

    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);

    if (!day || !month || !year || !timeMatch) {
      return null;
    }

    let hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);
    const meridiem = timeMatch[3];

    if (meridiem === 'AM') {
      hour = hour === 12 ? 0 : hour;
    } else if (meridiem === 'PM') {
      hour = hour === 12 ? 12 : hour + 12;
    }

    return {
      year,
      month,
      day,
      hour,
      minute
    };
  }

  private getCurrentLocalDateTimeParts(): LocalDateTimeParts {
    const now = new Date();

    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes()
    };
  }

  private compareLocalDateTimeParts(left: LocalDateTimeParts, right: LocalDateTimeParts): number {
    const keys: Array<keyof LocalDateTimeParts> = ['year', 'month', 'day', 'hour', 'minute'];

    for (const key of keys) {
      if (left[key] !== right[key]) {
        return left[key] - right[key];
      }
    }

    return 0;
  }

  // NEW SHOWTIME FLOW
  private extractDatesForDateFilter(performances: PerformanceGroup[], startPerformancesDate?: Date): void {
    let _tmpShowTimeDates = null;
    performances?.forEach((i: PerformanceGroup) => {
      if (i?.localTimes) {
        const localTimesArr = i.localTimes.split(',');

        localTimesArr?.forEach((j: string) => {
          const localFullDateStr = dateFormatter(j.split(' ')[0], 'dd-mm-yyyy', 'yyyy-mm-dd');
          const hourDateStr = j.split(' ')[1].replace(/(\d{1,2}:\d{2})(AM|PM)/g, '$1 $2');
          if (localFullDateStr) {
            if (!_tmpShowTimeDates) _tmpShowTimeDates = {};

            const isoDate: Date = convertToISODate(localFullDateStr, hourDateStr);

            // If a startPerformancesDate was provided, ignore any performance date before it
            if (startPerformancesDate && isoDate < startPerformancesDate) {
              return; // Skip this date
            }

            _tmpShowTimeDates[localFullDateStr] = isoDate.toLocaleDateString(
              this.translationService.currentLanguage().code,
              {
                weekday: 'short',
                day: '2-digit',
                month: 'short'
              }
            );
          }
        });
      }
    });
    if (_tmpShowTimeDates) {
      this.showTimeDates = { ..._tmpShowTimeDates };
    }
  }

  // NEW SHOWTIME FLOW

  private loadNearestCities(position: Position) {
    if (!this.diasbleNearestCities) {
      this.movieService
        .getNearestCities({
          movie: this.movie.id,
          latitude: position.lat,
          longitude: position.lng,
          radius: this.channelService.channel.settings?.showtimeRadius || 50000
        })
        .pipe(take(1))

        .subscribe(cities => {
          this.nearestCities = cities;
          this.addonLog();
          this.cd.detectChanges();
        });
    }
  }

  /**
   * @deprecard Don't found any use
   * @param location
   */
  handleLocationSelected(event: LocationComplete) {
    this.selectedLocationCountry = event.country;
    this.checkLocation();
    this.setInitialPosition();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platform);
  }

  evaluateDistanceUnits(performances: PerformanceGroup[] = []) {
    performances.forEach(perf => {
      if (
        this.location?.selectedCountry?.slug == 'united-states' ||
        this.location?.selectedCountry?.slug == 'united-kingdom'
      ) {
        perf.distanceLiteral = (perf.distance * 0.00062137119).toFixed(2) + ' mi';
      } else {
        perf.distanceLiteral = (perf.distance / 1000).toFixed(2) + ' km';
      }
    });
    return performances;
  }

  onBuyTicketHandle(event) {
    this.showtimeTicketing.next(event);

    //TODO Tracking
  }

  /**
   * @deprecard Don't found any use
   * @param index
   */
  clickPanel(index: number) {
    this.panels.forEach((p, i) => {
      if (i !== index) {
        this.cinema = this.performances()[i].cinema;
        this.panelEvents[i].hide.next();

        //TODO update this in master
      } else if (i !== this.indexPanelToOpen) {
        this.indexPanelToOpen = i;
      }
    });
  }

  /**
   * Updates tracking and url after a {@link PerformanceComponent} is open.
   *
   * //TODO barbeitoalex propostal - set event type
   * @param event
   */
  onCinemaOpened(event) {
    this.currentIndex = event.index;
    const openedCinema = this.performances()[event.index].cinema;
    this.debugLog('Tracking SelectCinema', {
      movie: this.movie?.title,
      cinema: openedCinema?.name,
      cinemaId: openedCinema?.id,
      index: event.index
    });

    //TODO Tracking

    // this.router.navigate([], {
    //   relativeTo: this.route,
    //   queryParams: queryParams
    // });

    if (this.enableRouterNavigation) {
      const queryParams: Params = { cinema: openedCinema.slug };

      if (this.nearestCitySetted) {
        queryParams.lat = this.nearestCitySetted.latitude;
        queryParams.lon = this.nearestCitySetted.longitude;
      } else if (!this.location.userLocation?.userLoc) {
        queryParams.lat = this.location.userLocation?.latitude;
        queryParams.lon = this.location.userLocation?.longitude;
      }
      const url = this.router.createUrlTree([], { relativeTo: this.route, queryParams: queryParams }).toString();
      this.loc.go(url);
    }

    this.trackingService.trackEvent({
      action: 'SelectCinema',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie.title} @ ${openedCinema.name}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie),
        cinema: openedCinema.name,
        cinema_city: openedCinema.city,
        idCinema: openedCinema.id
      }
    });

    this.cd.detectChanges();
  }

  /**
   * @docsNotRequired
   * @param event
   */
  onCinemaClosed() {
    this.currentIndex = -1;
    this.cd.detectChanges();
  }

  onNearCitySelected(city: NearCity) {
    this.debugLog('onNearCitySelected', {
      movie: this.movie?.title,
      city: city?.name,
      lat: city?.latitude,
      lng: city?.longitude
    });
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams.lat = city.latitude;
    queryParams.lng = city.longitude;
    this.nearestCitySetted = city;
    this.currentIndex = -1;
    this.loadPerformances(queryParams as Position);
    this.loadNearestCities(queryParams as Position);
    this.locationSelector.setCustomValue(`${city.name}, ${city.region.name}`);
    // update user location
    this.location.setUserLocation({
      latitude: city.latitude,
      longitude: city.longitude,
      address: `${city.name}, ${city.region.name}`
    });

    // track event
    this.trackingService.trackEvent({
      action: 'SelectCity',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie.title} @ ${city.name}`,
        movie: this.movie.title,
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie),
        genresMovie: getMovieGenres(this.movie)
      }
    });

    // check if the location selector is into the viewport
    const locationSelectorPosition = this.locationSelectorRef.nativeElement.getBoundingClientRect().top - 120;
    if (locationSelectorPosition < 0) {
      const wrapperPosition = this.wrapperRef.nativeElement.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: wrapperPosition, behavior: 'smooth' });
    }
    this.cd.detectChanges();
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(step, context ?? '');
  }

  /**
   * Used before the {@link onNearCitySelected} update the user location.
   * Don't have any use right now.
   */
  restoreInitialPosition(loadPerformances = true) {
    this.nearestCitySetted = undefined;
    this.location.setUserLocation(this.initialPosition);
    if (loadPerformances) {
      this.currentIndex = -1;
      const { latitude: lat, longitude: lng } = this.initialPosition;
      this.loadPerformances({ lat, lng });
      this.loadNearestCities({ lat, lng });
      this.locationSelector.setCustomValue(this.initialPosition.address);
    }
  }

  private resetLoadMoreVariables() {
    this.paginationCounter.set(0);
    this.cd.detectChanges();
  }

  public loadMore(): void {
    if (this.paginationCounter() * this.itemsPerPagination < this.performances().length) {
      this.paginationCounter.update(n => n + 1);
    }

    this.cd.detectChanges();
  }

  // NEW SHOWTIME FLOW
  updateCinemaListOnDateChange(dateString: string, trackSelection = false): void {
    // dateString expected to be 'yyyy-mm-dd'
    if (dateString) {
      const isoDate = convertToISODate(dateString, `00:00:00`);
      this.dateSelected = isoDate;
    }

    if (trackSelection && this.trackingService) {
      if (dateString) {
        this.trackingService.trackEvent({
          action: 'SelectDate',
          properties: {
            category: 'MovieShowtimes',
            label: `${this.movie.title} @ ${dateString}`,
            movie: this.movie.title,
            idMovie: this.movie.id,
            imdb: this.movie?.imdb,
            tagsMovie: getMovieTags(this.movie),
            genresMovie: getMovieGenres(this.movie),
            date: dateString
          }
        });
      } else {
        this.trackingService.trackEvent({
          action: 'SelectDate',
          properties: {
            category: 'MovieShowtimes',
            label: `${this.movie.title} @ no-date`,
            movie: this.movie.title,
            idMovie: this.movie.id,
            imdb: this.movie?.imdb,
            tagsMovie: getMovieTags(this.movie),
            genresMovie: getMovieGenres(this.movie),
            date: undefined
          }
        });
      }
    }
    this.loadingShowtimes.set(true);

    this.performancesUi.set([]);

    setTimeout(() => {
      this.resetLoadMoreVariables();
      this.loadMore();
      this.performancesUi.set(
        this.performances().filter((i: PerformanceGroup) => {
          const allLocalTimes = i.localTimes
            .split(',')
            ?.map(i => dateFormatter(i.split(' ')[0], 'dd-mm-yyyy', 'yyyy-mm-dd'));
          return allLocalTimes.includes(dateString);
        })
      );
      this.loadingShowtimes.set(false);
      this.previousPerformanceUiCount.set(this.performancesUi()?.length);
      this.cd.markForCheck();
    }, 0);
  }

  performanceTrackBy(index: number, item: PerformanceGroup): number {
    return item.id;
  }

  checkMovieIsOld() {
    const release = getDateFromDDMMYYYY(this.movie?.locReleaseDate || this.movie?.releaseDate);
    if (!release) return false;
    const today = new Date();
    const releaseDate = new Date(release);
    const diffTime = today.getTime() - releaseDate.getTime();
    const diffYears = diffTime / (1000 * 3600 * 24 * 365.25);
    this.hasMovieTwoYearsOld.set(diffYears >= 2);
    this.addonLog();
  }

  /**
   * This won't be remove yet for testing propouses
   * in the future but need to be commented in prod
   */
  addonLog() {
    // const cineamo =
    //   this.inCinemasAddon !== undefined &&
    //   this.loadingShowtimes === false &&
    //   this.hasMovieTwoYearsOld() === true &&
    //   (this.performances == undefined || this.performances.length === 0) &&
    //   (this.nearestCities == undefined || this.nearestCities.length === 0);
    // console.log('Cineamo Log ' + cineamo, {
    //   inCinemasAddon: this.inCinemasAddon !== undefined,
    //   loadingShowtimes: this.loadingShowtimes === false,
    //   hasMovieTwoYearsOld: this.hasMovieTwoYearsOld() === true,
    //   performances: this.performances == undefined || this.performances.length === 0,
    //   nearestCities: this.nearestCities == undefined || this.nearestCities.length === 0
    // });
  }

  get flowLabel() {
    if (this.isDateFlow) {
      return 'dates';
    }
    return 'location';
  }

  ngOnDestroy(): void {
    this.showtimesVisibilityHandle?.disconnect();
    this.structuredDataService.removeJsonLdScript('movie-events-jsonld');
  }

  private emitShowsLoadedIfNeeded(source: string): void {
    if (this.hasEmittedShowsLoaded) return;
    const performancesCount = this.performances().length;
    if (!performancesCount) return;

    this.debugLog('showsLoaded emit', {
      movie: this.movie?.title,
      performancesCount,
      hasObservers: this.showsLoaded.observed,
      source
    });
    this.showsLoaded.emit(performancesCount);
    this.hasEmittedShowsLoaded = true;
  }

  private trackShowtimesEventsFromResult(
    performancesCount: number,
    source: string,
    params?: PerformanceListWrapper
  ): void {
    if (!this.trackingService || !this.movie) return;

    const baseProperties = {
      event_category: 'MovieShowtimes',
      category: 'MovieShowtimes',
      label: `${this.movie.title}`,
      movie: this.movie.title,
      idMovie: this.movie.id,
      imdb: this.movie?.imdb,
      tagsMovie: getMovieTags(this.movie),
      genresMovie: getMovieGenres(this.movie),
      performances_count: performancesCount,
      source_component: 'showtime-in-cinemas',
      flow: this.showtimesFlow(),
      source
    };

    const loadedDedupeKey = this.buildLoadedEventDedupeKey('ShowtimesLoaded', performancesCount, params);
    const loadedTracked = this.trackingService.trackEventOnce(
      {
        action: 'ShowtimesLoaded',
        properties: baseProperties
      },
      loadedDedupeKey
    );
    this.debugLog(loadedTracked ? 'Tracking ShowtimesLoaded' : 'Skipping duplicate ShowtimesLoaded', {
      movie: this.movie.title,
      performancesCount,
      source,
      dedupeKey: loadedDedupeKey
    });

    if (performancesCount > 0) {
      const dedupeKey = this.buildLoadedEventDedupeKey('ShowtimesAvailable', performancesCount, params);
      const tracked = this.trackingService.trackEventOnce(
        {
          action: 'ShowtimesAvailable',
          properties: baseProperties
        },
        dedupeKey
      );
      this.debugLog(tracked ? 'Tracking ShowtimesAvailable' : 'Skipping duplicate ShowtimesAvailable', {
        movie: this.movie.title,
        performancesCount,
        source,
        dedupeKey
      });
    }
  }

  private buildLoadedEventDedupeKey(
    action: 'ShowtimesLoaded' | 'ShowtimesAvailable',
    performancesCount: number,
    params?: PerformanceListWrapper
  ): string {
    return [
      action,
      `movie:${this.movie?.id ?? this.movie?.friendlyUrl ?? ''}`,
      `flow:${this.showtimesFlow()}`,
      `params:${this.stableDedupeValue(params ?? {})}`,
      `count:${performancesCount}`
    ].join('|');
  }

  private stableDedupeValue(value: unknown): string {
    if (Array.isArray(value)) {
      return `[${value.map(item => this.stableDedupeValue(item)).join(',')}]`;
    }

    if (value && typeof value === 'object') {
      return `{${Object.keys(value as Record<string, unknown>)
        .sort()
        .map(key => `${key}:${this.stableDedupeValue((value as Record<string, unknown>)[key])}`)
        .join(',')}}`;
    }

    return String(value);
  }
}
